import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  DateRange as DateIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const MobileReceiptUploader = ({ report, onUploadComplete }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [receiptData, setReceiptData] = useState({
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0]
  });

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!receiptData.description || !receiptData.amount) {
      setMessage({ type: 'error', text: 'Please fill in description and amount before uploading' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', receiptData.description);
      formData.append('amount', receiptData.amount);
      formData.append('category', receiptData.category || 'General');
      formData.append('date', receiptData.date);

      await apiService.uploadExpenseReceipts(report.id, formData);
      
      setMessage({ type: 'success', text: 'Receipt uploaded successfully!' });
      setReceiptData({
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to upload receipt' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setReceiptData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <UploadIcon />
            </Avatar>
            <Typography variant="h6">
              Upload Receipt
            </Typography>
          </Box>

          {/* Receipt Details Form */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Description"
              value={receiptData.description}
              onChange={handleChange('description')}
              placeholder="What was this expense for?"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px'
                }
              }}
            />

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={receiptData.amount}
              onChange={handleChange('amount')}
              placeholder="0.00"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px'
                }
              }}
            />

            <TextField
              fullWidth
              label="Category"
              value={receiptData.category}
              onChange={handleChange('category')}
              placeholder="e.g., Fuel, Maintenance, Supplies"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px'
                }
              }}
            />

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={receiptData.date}
              onChange={handleChange('date')}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 48,
                  borderRadius: '12px'
                }
              }}
            />
          </Box>

          {/* Upload Button */}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="receipt-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={loading}
          />
          <label htmlFor="receipt-upload">
            <Button
              variant="contained"
              component="span"
              fullWidth
              disabled={loading || !receiptData.description || !receiptData.amount}
              startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
              sx={{
                minHeight: 56,
                borderRadius: 3,
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              {loading ? 'Uploading...' : 'Take Photo or Select Image'}
            </Button>
          </label>

          {message && (
            <Alert 
              severity={message.type} 
              sx={{ mt: 2, borderRadius: 2 }}
            >
              {message.text}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Upload Tips */}
      <Card sx={{ borderRadius: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            📸 Upload Tips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Ensure receipt is clearly visible and well-lit
            • Include all important details (amount, date, merchant)
            • Accepted formats: JPG, PNG, PDF
            • Maximum file size: 5MB
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MobileReceiptUploader; 